import React, { useEffect } from 'react';
import { User, Shield, Bell, Settings, Monitor, CreditCard, Lock } from 'lucide-react';
interface SettingsSidebarProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
  userType: 'angel' | 'customer';
}
export const SettingsSidebar: React.FC<SettingsSidebarProps> = ({
  activeSection,
  setActiveSection,
  userType
}) => {
  // Listen for events to set active section
  useEffect(() => {
    const handleSetActiveSection = (e: CustomEvent) => {
      if (e.detail && e.detail.section) {
        setActiveSection(e.detail.section);
      }
    };
    document.addEventListener('setActiveSettingsSection', handleSetActiveSection as EventListener);
    return () => {
      document.removeEventListener('setActiveSettingsSection', handleSetActiveSection as EventListener);
    };
  }, [setActiveSection]);
  return <div className="w-64 border-r border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 p-2 flex-shrink-0 overflow-y-auto" data-id="element-18917">
      <div className="flex flex-col space-y-1 py-1" data-id="element-18918">
        <h3 className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-18919">
          User Settings
        </h3>
        <button className={`flex items-center gap-3 px-3 py-2 rounded-lg text-left ${activeSection === 'profile' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'}`} onClick={() => setActiveSection('profile')} data-id="element-18920">
          <User size={18} data-id="element-18921" />
          <span data-id="element-18922">Profile</span>
        </button>
        <button className={`flex items-center gap-3 px-3 py-2 rounded-lg text-left ${activeSection === 'account' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'}`} onClick={() => setActiveSection('account')} data-id="element-18923">
          <Lock size={18} data-id="element-18924" />
          <span data-id="element-18925">Account</span>
        </button>
        <button className={`flex items-center gap-3 px-3 py-2 rounded-lg text-left ${activeSection === 'privacy' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'}`} onClick={() => setActiveSection('privacy')} data-id="element-18926">
          <Shield size={18} data-id="element-18927" />
          <span data-id="element-18928">Privacy & Safety</span>
        </button>
        {userType === 'angel' && <button className={`flex items-center gap-3 px-3 py-2 rounded-lg text-left ${activeSection === 'subscription' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'}`} onClick={() => setActiveSection('subscription')} data-id="element-18929">
            <CreditCard size={18} data-id="element-18930" />
            <span data-id="element-18931">Subscription Pricing</span>
          </button>}
        <h3 className="mt-4 px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-18932">
          App Settings
        </h3>
        <button className={`flex items-center gap-3 px-3 py-2 rounded-lg text-left ${activeSection === 'notifications' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'}`} onClick={() => setActiveSection('notifications')} data-id="element-18933">
          <Bell size={18} data-id="element-18934" />
          <span data-id="element-18935">Notifications</span>
        </button>
        <button className={`flex items-center gap-3 px-3 py-2 rounded-lg text-left ${activeSection === 'general' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'}`} onClick={() => setActiveSection('general')} data-id="element-18936">
          <Settings size={18} data-id="element-18937" />
          <span data-id="element-18938">General</span>
        </button>
        <button className={`flex items-center gap-3 px-3 py-2 rounded-lg text-left ${activeSection === 'display' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'}`} onClick={() => setActiveSection('display')} data-id="element-18939">
          <Monitor size={18} data-id="element-18940" />
          <span data-id="element-18941">Display</span>
        </button>
      </div>
      {/* User Info Preview */}
      <div className="mt-8 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm" data-id="element-18942">
        <div className="flex flex-col items-center" data-id="element-18943">
          <div className="relative" data-id="element-18944">
            <div className="w-16 h-16 rounded-full border-2 border-pink-500 dark:border-purple-500 p-1" data-id="element-18945">
              <img src={userType === 'angel' ? 'https://uploadthingy.s3.us-west-1.amazonaws.com/6iUmtnUqwDJqWS7bDhJ3vS/blue_male_eyes.jpg' : 'https://uploadthingy.s3.us-west-1.amazonaws.com/dw2TsUBK3FzXrUBc4rqEeR/male-avatar.jpg'} alt="Avatar Preview" className="w-full h-full object-cover rounded-full" data-id="element-18946" />
            </div>
            <div className="absolute -bottom-1 -right-1 bg-green-500 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800" data-id="element-18947"></div>
          </div>
          <span className="mt-2 font-medium text-gray-800 dark:text-gray-200 text-sm" data-id="element-18948">
            {userType === 'angel' ? 'GoldenShowerTina' : 'Mitch'}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-18949">
            {userType === 'angel' ? 'Angel' : 'Customer'} • Online
          </span>
        </div>
      </div>
    </div>;
};